<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { TYPE, LANGUAGE } from '@/env';
import Banner from '@/components/Banner.vue';
import OtherAppDash from '@/components/OtherAppDash.vue';
import { traceAsync } from '@/otel';
import { getBanner } from '@/api/banners/getBanner';
import type { BannerUnified } from '@/api/banners/types';
import { contact } from '@/storage/contact';
import { footerMenu } from '@/modules/FooterMenu';

const banner = reactive<BannerUnified>({ url: '', desktop: null, mobile: null });
const existsBanner = ref(false);

const loadBanner = () => {
    return traceAsync('Footer.loadBanner', async () => {
        const bannerData = await getBanner(LANGUAGE, 'footer');
        if (bannerData) {
            Object.assign(banner, bannerData);
        }
        existsBanner.value =
            !!(banner.desktop && banner.mobile);
    });
};


onMounted(() => {
    traceAsync('Footer.mounted', () => Promise.all([loadBanner()]));
});
</script>
<template>
    <footer class="footer">
        <section class="footer__section footer__section--contact">
            <div class="footer__section-inner footer__section-inner--left">
                <h2 class="footer__heading-2">
                    {{ $t('TEXTS.call-and-hours') }}
                </h2>
                <h3 class="footer__heading-3">
                    <a :href="`tel:${contact[LANGUAGE].phone}`" class="footer__tel">{{
                        contact[LANGUAGE].phone }}</a>
                </h3>
                <a :href="`mailto:${contact[LANGUAGE].email}`" class="footer__link footer__link--darker">{{
                    contact[LANGUAGE].email }}</a>
            </div>
        </section>
        <section class="footer__section footer__section--contact">
            <div class="footer__section-inner">
                <h2 class="footer__heading-2">
                    {{
                        TYPE === 'cruises'
                            ? $t('TEXTS.follow-cruises-on-socials')
                            : $t('TEXTS.follow-on-socials')
                    }}
                </h2>
                <div class="footer__socials">
                    <a :href="contact[LANGUAGE].facebook" class="footer__social" target="_blank" aria-label="Facebook">
                        <svg class="footer__social-icon">
                            <use href="@/assets/imgs/icons/sprite.svg#facebook"></use>
                        </svg>
                    </a>
                    <a :href="contact[LANGUAGE].instagram" class="footer__social" target="_blank"
                        aria-label="Instagram">
                        <svg class="footer__social-icon">
                            <use href="@/assets/imgs/icons/sprite.svg#instagram"></use>
                        </svg>
                    </a>
                </div>
            </div>
        </section>
        <section class="footer__section footer__section--other-app">
            <div class="footer__section-inner">
                <OtherAppDash></OtherAppDash>
            </div>
        </section>
        <section class="footer__section footer__section--banner" v-if="existsBanner">
            <Banner v-if="banner.desktop && banner.mobile && banner.url" class="footer__banner"
                :type="banner.desktop.type" :type-mobile="banner.mobile.type" :source="banner.desktop.source"
                :source-mobile="banner.mobile.source" :url="banner.url">
            </Banner>
        </section>
        <section class="footer__section footer__section--links">
            <div class="footer__links-container">
                <RouterLink v-for="(item, i) in footerMenu" :key="i" :to="item.url" class="footer__link">{{
                    item.name[LANGUAGE]
                    }}</RouterLink>
            </div>
        </section>
        <section class="footer__section footer__section--copyright">
            <p class="footer__copyright">
                © <span class="js-footer-copyright">2015 - 2025 </span>
                <RouterLink to="/" class="footer__link">nautyy.{{
                    LANGUAGE === 'cs' ? 'cz' : 'sk'
                    }}</RouterLink> - {{ $t('TEXTS.footer-note') }}.
            </p>
        </section>
    </footer>
</template>
